import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../data/mock';
import { Check, Sparkles, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const Products = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-purple-600 via-purple-700 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-purple-300" />
            <span className="text-sm text-white/90">Our Products</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-extralight mb-6 tracking-tight">
            Intelligent Platforms
            <br />
            <span className="text-purple-200">Built for the Future</span>
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto font-light">
            AI-powered SaaS solutions that transform how businesses operate, analyze, and grow
          </p>
        </div>
      </section>

      {/* Products */}
      {products.map((product, index) => (
        <section
          key={product.id}
          id={product.name.toLowerCase().replace(/\./g, '').replace(/\s/g, '')}
          className={`py-32 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className={`grid md:grid-cols-2 gap-16 items-center ${
              index % 2 === 1 ? 'md:flex-row-reverse' : ''
            }`}>
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-6">
                  {product.tagline}
                </div>
                <h2 className="text-5xl md:text-6xl font-light mb-6 tracking-tight">
                  {product.name}
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {product.description}
                </p>
                <p className="text-base text-gray-600 leading-relaxed mb-8">
                  {product.fullDescription}
                </p>
                
                <div className="space-y-4 mb-8">
                  <h3 className="text-xl font-medium text-gray-900">Key Features</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-purple-600" />
                        </div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  size="lg"
                  onClick={() => setIsDemoModalOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-8 transition-all hover:scale-105"
                >
                  Request Demo
                </Button>
              </div>
              
              <div className={`relative h-[500px] rounded-3xl overflow-hidden shadow-2xl ${
                index % 2 === 1 ? 'md:order-1' : ''
              }`}>
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-32 bg-gradient-to-br from-gray-900 via-purple-900 to-purple-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-light mb-6 tracking-tight">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-white/80 mb-12 font-light">
            Discover how our AI-powered platforms can revolutionize your operations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => setIsDemoModalOpen(true)}
              className="bg-white text-purple-700 hover:bg-white/90 rounded-full px-8"
            >
              Schedule a Demo
            </Button>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Demo Request Modal */}
      <Dialog open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light text-center mb-2">Coming Soon</DialogTitle>
            <DialogDescription className="text-center text-base">
              We'll be accepting demo requests very soon. Thank you for staying with us!
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-6">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-purple-600" />
            </div>
          </div>
          <Button
            onClick={() => setIsDemoModalOpen(false)}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full"
          >
            Got it, thanks!
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;